// ==============================================================================
// CUSTOM SUPABASE CLIENT ADAPTER - MYSQL BRIDGE
// ==============================================================================

// Determine API URL: Use relative path in production, and Domaine Numidya production URL in development
const API_URL = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  ? 'https://domaine.numidya.pro/api/index.php'
  : '/api/index.php';

class SupabaseQueryBuilder {
  constructor(tableName, url) {
    this.tableName = tableName;
    this.url = url;
    this.operation = 'select'; // select, insert, update, delete
    this.selectColumns = '*';
    this.insertData = null;
    this.updateData = null;
    this.filters = [];
    this.orders = [];
    this.limitVal = null;
    this.isSingle = false;
    this.isMaybeSingle = false;
  }

  select(columns = '*') {
    // If the operation is already insert, update, or delete, do not override it.
    if (this.operation !== 'insert' && this.operation !== 'update' && this.operation !== 'delete') {
      this.operation = 'select';
    }
    this.selectColumns = columns;
    return this;
  }

  insert(data) {
    this.operation = 'insert';
    this.insertData = Array.isArray(data) ? data : [data];
    return this;
  }

  update(data) {
    this.operation = 'update';
    this.updateData = data;
    return this;
  }

  delete() {
    this.operation = 'delete';
    return this;
  }

  eq(column, value) {
    this.filters.push({ column, operator: 'eq', value });
    return this;
  }

  neq(column, value) {
    this.filters.push({ column, operator: 'neq', value });
    return this;
  }

  in(column, value) {
    this.filters.push({ column, operator: 'in', value });
    return this;
  }

  order(column, { ascending = true } = {}) {
    this.orders.push({ column, ascending });
    return this;
  }

  limit(limit) {
    this.limitVal = limit;
    return this;
  }

  single() {
    this.isSingle = true;
    return this;
  }

  maybeSingle() {
    this.isMaybeSingle = true;
    return this;
  }

  async execute() {
    try {
      const token = localStorage.getItem('supabase_session_token');
      const response = await fetch(this.url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({
          action: 'query',
          table: this.tableName,
          operation: this.operation,
          selectColumns: this.selectColumns,
          insertData: this.insertData,
          updateData: this.updateData,
          filters: this.filters,
          orders: this.orders,
          limit: this.limitVal,
          single: this.isSingle,
          maybeSingle: this.isMaybeSingle
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch(e) {}
        return { data: null, error: { message: errorData?.error?.message || errorText || 'Network error' } };
      }

      const result = await response.json();
      return result;
    } catch (err) {
      return { data: null, error: { message: err.message } };
    }
  }

  then(onFulfilled, onRejected) {
    return this.execute().then(onFulfilled, onRejected);
  }
}

class SupabaseStorageBucket {
  constructor(bucketName, apiUrl) {
    this.bucketName = bucketName;
    this.apiUrl = apiUrl;
  }

  async upload(filePath, file) {
    try {
      const token = localStorage.getItem('supabase_session_token');
      const formData = new FormData();
      formData.append('action', 'upload');
      formData.append('bucket', this.bucketName);
      formData.append('filePath', filePath);
      formData.append('file', file);

      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: formData
      });

      if (!response.ok) {
        const errorText = await response.text();
        return { data: null, error: { message: errorText || 'Network error' } };
      }

      const result = await response.json();
      return result;
    } catch (err) {
      return { data: null, error: { message: err.message } };
    }
  }

  getPublicUrl(filePath) {
    let apiBaseUrl = this.apiUrl;
    // Resolve absolute URL dynamically to handle multi-subdomain installations
    if (apiBaseUrl.startsWith('/')) {
      apiBaseUrl = window.location.origin + apiBaseUrl;
    }
    const apiDir = apiBaseUrl.substring(0, apiBaseUrl.lastIndexOf('/'));
    const cleanPath = filePath.replace(/\\/g, '/');
    const publicUrl = `${apiDir}/uploads/${this.bucketName}/${cleanPath}`;
    return { data: { publicUrl } };
  }

  async remove(fileNames) {
    try {
      const token = localStorage.getItem('supabase_session_token');
      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({
          action: 'delete_file',
          bucket: this.bucketName,
          fileNames: fileNames
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        return { data: null, error: { message: errorText || 'Network error' } };
      }

      const result = await response.json();
      return result;
    } catch (err) {
      return { data: null, error: { message: err.message } };
    }
  }
}

class SupabaseStorage {
  constructor(apiUrl) {
    this.apiUrl = apiUrl;
  }

  from(bucketName) {
    return new SupabaseStorageBucket(bucketName, this.apiUrl);
  }
}

class SupabaseAuth {
  constructor(apiUrl) {
    this.apiUrl = apiUrl;
    this.listeners = new Set();
  }

  async getSession() {
    const token = localStorage.getItem('supabase_session_token');
    const userStr = localStorage.getItem('supabase_session_user');
    if (!token || !userStr) {
      return { data: { session: null }, error: null };
    }
    try {
      const user = JSON.parse(userStr);
      return { data: { session: { user, access_token: token } }, error: null };
    } catch (err) {
      return { data: { session: null }, error: null };
    }
  }

  async getUser() {
    const sessionRes = await this.getSession();
    if (sessionRes.data.session) {
      return { data: { user: sessionRes.data.session.user }, error: null };
    }
    return { data: { user: null }, error: null };
  }

  async signInWithPassword({ email, password }) {
    try {
      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'login', email, password })
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch(e) {}
        return { data: null, error: { message: errorData?.error?.message || errorText || 'Connection failed' } };
      }

      const result = await response.json();
      if (result.error) {
        return { data: null, error: result.error };
      }

      const { user, token } = result.data;
      localStorage.setItem('supabase_session_token', token);
      localStorage.setItem('supabase_session_user', JSON.stringify(user));

      this._notifyListeners('SIGNED_IN', { user, access_token: token });

      return { data: { user, session: { user, access_token: token } }, error: null };
    } catch (err) {
      return { data: null, error: { message: err.message } };
    }
  }

  async signUp({ email, password, options = {} }) {
    try {
      const fullName = options?.data?.full_name || '';
      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'signup', email, password, fullName })
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch(e) {}
        return { data: null, error: { message: errorData?.error?.message || errorText || 'Registration failed' } };
      }

      const result = await response.json();
      if (result.error) {
        return { data: null, error: result.error };
      }

      return { data: { user: result.data.user }, error: null };
    } catch (err) {
      return { data: null, error: { message: err.message } };
    }
  }

  async signOut() {
    localStorage.removeItem('supabase_session_token');
    localStorage.removeItem('supabase_session_user');
    this._notifyListeners('SIGNED_OUT', null);
    return { error: null };
  }

  onAuthStateChange(callback) {
    this.listeners.add(callback);
    this.getSession().then(({ data: { session } }) => {
      const event = session ? 'SIGNED_IN' : 'SIGNED_OUT';
      callback(event, session);
    });

    return {
      data: {
        subscription: {
          unsubscribe: () => {
            this.listeners.delete(callback);
          }
        }
      }
    };
  }

  _notifyListeners(event, session) {
    for (const listener of this.listeners) {
      try {
        listener(event, session);
      } catch (err) {
        console.error('Error in auth state change listener:', err);
      }
    }
  }
}

class SupabaseClient {
  constructor(url) {
    this.url = url;
    this.auth = new SupabaseAuth(url);
    this.storage = new SupabaseStorage(url);
  }

  from(tableName) {
    return new SupabaseQueryBuilder(tableName, this.url);
  }
}

export const supabase = new SupabaseClient(API_URL);
export default supabase;